import { IconButton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Fragment, MouseEvent, ReactNode, useContext } from 'react';

import { useCopy } from 'utils/useCopy';

import styles from './summaryItems.module.scss';
import { UserActivityLayoutContext } from './userActivityLayoutContext';

export type SummaryItem = {
  /** Stable key for the segment. */
  readonly key: string;
  /** Rendered content (text, or a link for e.g. a tracking URL). Empty/absent segments are dropped. */
  readonly content?: ReactNode;
  /** When set, renders a copy-to-clipboard button next to the segment. */
  readonly copyText?: string;
  /** When set, shown as a tooltip on hover (e.g. "Order ID" for an opaque id). */
  readonly label?: string;
};

const hasContent = (item: SummaryItem): boolean =>
  item.content !== undefined && item.content !== null && item.content !== '';

type CopyButtonProps = {
  readonly textToCopy: string;
};

const CopyButton = ({ textToCopy }: CopyButtonProps): JSX.Element => {
  const [copy, { status }] = useCopy();

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    copy(textToCopy);
  };

  return (
    <IconButton
      className={styles.copyButton}
      icon={status === 'copied' ? 'done' : 'copy'}
      iconSize={12}
      variant="tertiary"
      onClick={handleClick}
    />
  );
};

type SummaryItemsProps = {
  readonly items: readonly SummaryItem[];
};

export const SummaryItems = ({
  items,
}: SummaryItemsProps): JSX.Element | null => {
  const layout = useContext(UserActivityLayoutContext);
  const shown = items.filter(hasContent);
  if (!shown.length) {
    return null;
  }

  return (
    <span
      className={classnames(styles.items, {
        [styles.stacked]: layout === 'compact',
      })}
    >
      {shown.map((item, index) => (
        <Fragment key={item.key}>
          {index > 0 && layout === 'wide' && (
            <span className={styles.separator}>·</span>
          )}
          <span className={styles.item}>
            <Tooltip content={item.label}>{item.content}</Tooltip>
            {item.copyText && <CopyButton textToCopy={item.copyText} />}
          </span>
        </Fragment>
      ))}
    </span>
  );
};
